import { useState } from "react";
import { Volume2, Bell, Save, Moon, Sun, Trash2, AlertTriangle } from "lucide-react";
import { settingsAPI, progressAPI, authAPI } from "../api";
import { requestNotificationPermission, scheduleReminder, cancelReminder } from "../notifications";
import Chip from "../components/atoms/Chip";
import Button from "../components/atoms/Button";

export default function Settings({ user, darkMode, setDarkMode, onLogout }) {
  const [speechRate, setSpeechRate] = useState(() => Number(localStorage.getItem("speechRate") || "0.85"));
  const [notifications, setNotifications] = useState(() => localStorage.getItem("notifications") !== "false");
  const [saved, setSaved] = useState(false);

  async function handleSave() {
    localStorage.setItem("speechRate", String(speechRate));
    localStorage.setItem("notifications", String(notifications));
    try { await settingsAPI.update({ speechRate, notifications }); } catch {}
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  }

  async function handleResetProgress() {
    if (window.confirm("Reset all your progress to Day 1? This can't be undone.")) {
      try { await progressAPI.reset(); } catch {}
      localStorage.setItem("currentDay", "1");
      window.location.reload();
    }
  }

  async function handleDeleteAccount() {
    if (window.confirm("Permanently delete your account and all data?")) {
      try { await authAPI.deleteAccount(); } catch {}
      onLogout();
    }
  }

  async function toggleNotifications() {
    const next = !notifications;
    if (next) {
      const granted = await requestNotificationPermission();
      if (granted) { scheduleReminder(19, 0); setNotifications(true); }
      else { alert("Please allow notifications in your browser settings."); }
    } else {
      cancelReminder();
      setNotifications(false);
    }
  }

  return (
    <div className="page-enter" style={{ paddingBottom: 48, display: "flex", flexDirection: "column", gap: 32 }}>
      <div>
        <Chip tone="gold">Settings</Chip>
        <h1 className="font-display" style={{ fontSize: "clamp(40px, 6vw, 56px)", lineHeight: 1.05, fontWeight: 400, letterSpacing: "-0.025em", margin: "12px 0 8px" }}>
          Tune the <em style={{ color: "var(--gold-500)" }}>knobs.</em>
        </h1>
        <p style={{ color: "var(--fg-2)", fontSize: 16, maxWidth: "60ch", lineHeight: 1.6, margin: 0 }}>
          A short list. Most defaults are intentional — change only what you need.
        </p>
      </div>

      <Section icon={<Volume2 size={18} strokeWidth={1.5} color="var(--rust)" />} title="Speech">
        <Row label="Voice speed" hint={`${speechRate.toFixed(2)}× — Slow at 0.5×, normal at 1×.`}>
          <input
            type="range"
            min="0.5"
            max="1.5"
            step="0.05"
            value={speechRate}
            onChange={(e) => setSpeechRate(Number(e.target.value))}
            style={{ width: 200, accentColor: "var(--gold-500)" }}
          />
        </Row>
      </Section>

      <Section icon={<Bell size={18} strokeWidth={1.5} color="var(--gold-500)" />} title="Notifications">
        <Row label="Daily reminder" hint="A nudge at 7pm if you haven't practiced.">
          <Toggle on={notifications} onClick={toggleNotifications} />
        </Row>
      </Section>

      <Section icon={darkMode ? <Sun size={18} strokeWidth={1.5} color="var(--gold-500)" /> : <Moon size={18} strokeWidth={1.5} color="var(--ink-700)" />} title="Appearance">
        <Row label="Dark mode" hint="Coming soon — currently switches background to ink.">
          <Toggle on={darkMode} onClick={() => setDarkMode && setDarkMode(!darkMode)} />
        </Row>
      </Section>

      {user && (
        <Section title="Account" hint={`Signed in as ${user.email}`}>
          <Row label="Save changes">
            <Button variant="primary" onClick={handleSave} icon={<Save size={14} strokeWidth={1.8} />}>
              {saved ? "Saved" : "Save"}
            </Button>
          </Row>
        </Section>
      )}

      {/* Danger zone */}
      <div style={{ borderTop: "1px solid var(--berry-light)", paddingTop: 24, marginTop: 8 }}>
        <h2 className="font-display" style={{ fontSize: 24, fontWeight: 400, color: "var(--berry-dark)", margin: "0 0 4px", display: "flex", alignItems: "center", gap: 10 }}>
          <AlertTriangle size={20} strokeWidth={1.5} /> Danger zone
        </h2>
        <p style={{ color: "var(--fg-3)", fontSize: 13, margin: "0 0 18px" }}>These actions can't be undone.</p>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
          <button
            type="button"
            onClick={handleResetProgress}
            style={{ ...dangerBtn, background: "transparent", color: "var(--berry)" }}
          >
            <Trash2 size={14} strokeWidth={1.5} /> Reset all progress
          </button>
          <button
            type="button"
            onClick={handleDeleteAccount}
            style={{ ...dangerBtn, background: "var(--berry)", color: "var(--paper)", borderColor: "var(--berry-dark)" }}
          >
            <Trash2 size={14} strokeWidth={1.5} /> Delete account
          </button>
        </div>
      </div>
    </div>
  );
}

function Section({ icon, title, hint, children }) {
  return (
    <div style={{ borderBottom: "1px solid var(--border-1)", paddingBottom: 24 }}>
      <h2 className="font-display" style={{ fontSize: 28, fontWeight: 400, lineHeight: 1.15, margin: "0 0 4px", display: "flex", alignItems: "center", gap: 10 }}>
        {icon}{title}
      </h2>
      {hint && <p style={{ color: "var(--fg-3)", fontSize: 13, margin: "0 0 14px" }}>{hint}</p>}
      <div style={{ marginTop: hint ? 6 : 14 }}>{children}</div>
    </div>
  );
}

function Row({ label, hint, children }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 0", gap: 16, flexWrap: "wrap" }}>
      <div style={{ flex: 1, minWidth: 200 }}>
        <p style={{ fontWeight: 600, fontSize: 14, color: "var(--fg-1)", margin: 0 }}>{label}</p>
        {hint && <p style={{ color: "var(--fg-3)", fontSize: 12, margin: "4px 0 0" }}>{hint}</p>}
      </div>
      <div>{children}</div>
    </div>
  );
}

function Toggle({ on, onClick }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      onClick={onClick}
      style={{
        position: "relative",
        width: 44,
        height: 24,
        borderRadius: 999,
        background: on ? "var(--gold-500)" : "var(--ink-300)",
        border: 0,
        cursor: "pointer",
        transition: "background 150ms cubic-bezier(.2,.8,.2,1)",
      }}
    >
      <span style={{
        position: "absolute",
        top: 2,
        left: on ? 22 : 2,
        width: 20,
        height: 20,
        borderRadius: 999,
        background: "#fff",
        boxShadow: "0 1px 2px rgba(11,17,29,.2)",
        transition: "left 150ms cubic-bezier(.2,.8,.2,1)",
      }} />
    </button>
  );
}

const dangerBtn = {
  display: "inline-flex",
  alignItems: "center",
  gap: 8,
  padding: "10px 14px",
  borderRadius: "var(--radius-md)",
  border: "1px solid var(--berry-light)",
  fontSize: 14,
  fontWeight: 600,
  cursor: "pointer",
  fontFamily: "var(--font-body)",
  transition: "filter 150ms cubic-bezier(.2,.8,.2,1)",
};
